import { ComponentProps, PropsWithChildren } from "react";

export interface FormInputOwnProps<TElement = HTMLInputElement> {
  id?: string;
  name: string;
  label: string;
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  fieldError?: string;
  list?: string;
  onChange?: (event: React.ChangeEvent<TElement>) => void;
  onPaste?: (event: React.ClipboardEvent<TElement>) => void;
  inputType?: ComponentProps<"input">["type"];
  placeholder?: string;
  description?: string;
}

export function FormInput({
  id,
  name,
  label,
  defaultValue,
  fieldError,
  disabled,
  readOnly,
  list,
  inputType = "text",
  onChange,
  onPaste,
  placeholder,
  description,
}: FormInputOwnProps) {
  return (
    <>
      <label className="text-sm font-bold" data-forminput>
        {label}:{" "}
        <input
          className="block text-sm border-2 py-1 px-2 border-gray-200 focus:border-primary-500 w-full"
          name={name}
          id={id}
          type={inputType}
          list={list}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          defaultValue={defaultValue}
          onPaste={onPaste}
          placeholder={placeholder}
          aria-invalid={Boolean(fieldError) || undefined}
          aria-describedby={fieldError ? `${name}-error` : undefined}
        />
      </label>
      {fieldError ? (
        <p className="text-sm text-red-500" role="alert" id={`${name}-error`}>
          {fieldError}
        </p>
      ) : null}
      {description ? (
        <p className="text-sm text-gray-500" role="alert" id={`${name}-error`}>
          {description}
        </p>
      ) : null}
    </>
  );
}

export function FormCheckbox({
  id,
  name,
  label,
  defaultChecked,
  fieldError,
  disabled,
  readOnly,
  list,
  onChange,
}: Omit<FormInputOwnProps, "inputType" | "defaultValue"> & {
  defaultChecked?: boolean;
}) {
  return (
    <>
      <label
        className="flex flex-row items-center text-sm font-bold my-2"
        data-forminput
      >
        <input
          className="text-sm border-2 py-1 px-2 mr-2 border-gray-200 focus:border-primary-500"
          name={name}
          id={id}
          type="checkbox"
          list={list}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          defaultChecked={defaultChecked}
          aria-invalid={Boolean(fieldError) || undefined}
          aria-describedby={fieldError ? `${name}-error` : undefined}
        />
        {label}
      </label>
      {fieldError ? (
        <p className="text-sm text-red-500" role="alert" id={`${name}-error`}>
          {fieldError}
        </p>
      ) : null}
    </>
  );
}

export function FormSelect({
  id,
  name,
  label,
  defaultValue,
  fieldError,
  disabled,
  onChange,
  children,
}: PropsWithChildren<FormInputOwnProps<HTMLSelectElement>>) {
  return (
    <>
      <label className="text-sm font-bold" data-forminput>
        {label}:{" "}
        <select
          onChange={onChange}
          className="block text-sm border-2 py-1 px-2 border-gray-200 focus:border-primary-500 w-full"
          id={name}
          name={name}
          disabled={disabled}
          defaultValue={defaultValue}
          aria-invalid={Boolean(fieldError) || undefined}
          aria-describedby={fieldError ? `${name}-error` : undefined}
        >
          {children}
        </select>
      </label>
      {fieldError ? (
        <p className="text-sm text-red-500" role="alert" id={`${name}-error`}>
          {fieldError}
        </p>
      ) : null}
    </>
  );
}

export function FormTextarea({
  id,
  name,
  label,
  defaultValue,
  fieldError,
}: FormInputOwnProps) {
  return (
    <>
      <label className="text-sm font-bold" data-forminput>
        {label}:{" "}
        <textarea
          className="block text-sm border-2 py-1 px-2 border-gray-200 focus:border-primary-500 w-full"
          name={name}
          id={id}
          rows={6}
          defaultValue={defaultValue}
          aria-invalid={Boolean(fieldError) || undefined}
          aria-describedby={fieldError ? `${name}-error` : undefined}
        />
      </label>
      {fieldError ? (
        <p className="text-red-500" role="alert" id={`${name}-error`}>
          {fieldError}
        </p>
      ) : null}
    </>
  );
}

export function FormField({
  name,
  label,
  fieldError,
  children,
}: PropsWithChildren<FormInputOwnProps>) {
  return (
    <>
      <label className="text-sm font-bold" data-forminput>
        {label}:{" "}
        <div className="block text-sm border-2 py-1 px-2 border-gray-200 focus:border-primary-500 w-full">
          {children}
        </div>
      </label>
      {fieldError ? (
        <p className="text-red-500" role="alert" id={`${name}-error`}>
          {fieldError}
        </p>
      ) : null}
    </>
  );
}
